import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import memoizeOne from 'memoize-one';
import { isEmpty } from 'lodash';
import isDeepEqual from 'lodash.isequal';
import { assoc,  pick, reduce, sortBy, prop, equals, omit } from 'ramda';
import { MAX_MINUTES_TO_CHAIN_MESSAGES } from '../../constants/AppConstants';

import SystemMessage from '../SystemMessage';
import Message from '../Message/MessageContainer';
import MessageSeparator from './MessageSeparator';

import { getFormattedDate } from '../../helpers/DateUtils';
import { isUserMessage } from '../../helpers/UserUtils';

const getFirstMessageInDateGroupId = messagesDateGroup => Object.keys(messagesDateGroup)[0];

class MessagesList extends Component {
  // NOTE: If adding a new prop, shouldComponentUpdate needs to be evaluated.
  // If the property changing should rerender this component, a comparison is needed.
  static propTypes = {
    messages: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      chatId: PropTypes.number,
      content: PropTypes.string,
      contentType: PropTypes.string,
      senderId: PropTypes.number,
      attachments: PropTypes.array,
    }),
    chat: PropTypes.shape({
      id: PropTypes.number,
      users: PropTypes.array,
      name: PropTypes.string,
      type: PropTypes.string,
      firstUnreadMessageId: PropTypes.number,
    }),
    selectedMessages: PropTypes.object,
    hideDateSeparator: PropTypes.bool,
    firstUnreadMessageId: PropTypes.string,
    attachments: PropTypes.object,
    users: PropTypes.object,
  };

  shouldComponentUpdate(nextProps) {
    const { messages, hideDateSeparator, firstUnreadMessageId, users, attachments, chat, selectedMessages, isSelectingMessages } = this.props;

    // Typing/Scrolling is causing unnecessary re-renders by the parent. Neither changes what we render in this component.
    const beforeChat = omit(['typingUsers', 'scrollPosition'], chat);
    const afterChat = omit(['typingUsers', 'scrollPosition'], nextProps.chat);

    return !equals(messages, nextProps.messages)
      || !equals(hideDateSeparator, nextProps.hideDateSeparator)
      || !equals(firstUnreadMessageId, nextProps.firstUnreadMessageId)
      || !equals(users, nextProps.users)
      || !equals(attachments, nextProps.attachments)
      || !equals(beforeChat, afterChat)
      || !equals(selectedMessages, nextProps.selectedMessages)
      || !equals(isSelectingMessages, nextProps.isSelectingMessages);
  }

  render() {
    const { messages, hideDateSeparator, firstUnreadMessageId, users, attachments, chat, selectedMessages, isSelectingMessages } = this.props;

    // Returns Key Value Pairs, Date => Array of Messages ordered by TimeSent desc
    const messagesDateGroups = !isEmpty(messages) ? memoizeSortAndGroupMessagesByDateAndUser(messages) : {};

    return (
      <Fragment>
        {Object.entries(messagesDateGroups).map(([dateKey, dateMessages]) => (
          <div className="messages-list" key={dateKey} data-qa={'messageDateGroup'}>
            <MessageSeparator
              date={hideDateSeparator ? null : dateKey}
              isNewMessage={getFirstMessageInDateGroupId(dateMessages) === firstUnreadMessageId}
            />
            {dateMessages.map(message =>
              <Fragment key={message.id}>
                <MessageSeparator isNewMessage={message.id !== getFirstMessageInDateGroupId(dateMessages) && message.id === firstUnreadMessageId}/>
                {renderMessage(message, users, attachments, chat, selectedMessages, isSelectingMessages)}
              </Fragment>
            )}
          </div>
        ))}
      </Fragment>
    );
  }
}

const renderMessage = (message, users, allAttachments, chat, selectedMessages, isSelectingMessages) => {
  if (isUserMessage(message)) {
    const messageAttachmentIds = message.attachments;
    const attachments = pick(messageAttachmentIds, allAttachments);
    const sender = users[message.senderId];

    return (
      <Message
        message={message}
        attachments={attachments}
        sender={sender}
        selectedMessages={selectedMessages}
        isSelectingMessages={isSelectingMessages}
      />
    );
  } else {
    return (
      <SystemMessage
        message={message}
        users={users}
        chat={chat}
        isSelectingMessages={isSelectingMessages}
      />
    );
  }
};

// Memoized so we don't resort and regroup multiple times when the same messages are seen
const memoizeSortAndGroupMessagesByDateAndUser = memoizeOne(messages => sortAndGroupByDateAndUser(messages), isDeepEqual);

const sortAndGroupByDateAndUser = (messages) => {
  const sortedMessages = sortBy(prop('timeSent'))(Object.values(messages));

  const groupedByDate = reduce((acc, message) => {
    const date = getFormattedDate(message.timeSent);
    const array = acc[date] || [];
    return assoc(date, [...array, message], acc);
  }, {}, sortedMessages);

  Object.values(groupedByDate).forEach(dateGroup => groupByUser(dateGroup));

  return groupedByDate;
};

const groupByUser = (messages) => {
  const arrayOfMessages = messages;
  for (let i = 1; i < arrayOfMessages.length; i++) {
    const prevMessage = arrayOfMessages[i - 1];
    const currentMessage = arrayOfMessages[i];
    const currentMessageTimeSentMoment = moment(currentMessage.timeSent);
    const prevMessageTimeSentMoment = moment(prevMessage.timeSent);

    // Chain the message with the last message if it is the same user and within a certain time of the last message
    if (isUserMessage(currentMessage) && isUserMessage(prevMessage) &&
      !currentMessage.isFailed && !prevMessage.isFailed &&
      currentMessage.senderId === prevMessage.senderId &&
      currentMessageTimeSentMoment.diff(prevMessageTimeSentMoment, 'minutes') <= MAX_MINUTES_TO_CHAIN_MESSAGES
    ) {
      currentMessage.isChained = true;

      if (!prevMessage.isChained) {
        prevMessage.isFirstInChainedMessage = true;
      }

      // Show the time if it is not in the same minute as the previous message
      if (currentMessageTimeSentMoment.minute() !== prevMessageTimeSentMoment.minute()) {
        currentMessage.shouldShowTimeSent = true;
      }
    }
  }
};

export default MessagesList;
